import {
  ThumbDown as ThumbDownIcon,
  ThumbUp as ThumbUpIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { styled, Theme, useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { feedbackApi } from '../../api/feedback';
import { suggestionApi } from '../../api/suggestion';
import { userApi } from '../../api/user';
import Iconify from '../../components/iconify';
import {
  PET_CATEGORY_TRANSLATIONS,
  PET_FACT_TYPE_TRANSLATIONS,
} from '../../constants/petFactTranslations';
import { PATH_DASHBOARD } from '../../routes/paths';
import { fDate } from '../../utils/formatTime';
import { translateKey } from 'src/locales/translate';


const TagChip = styled(Chip)(({ theme }: { theme: Theme }) => ({
  borderRadius: 16,
  height: 24,
  fontSize: 12,
  fontWeight: 600,
}));

// Define pet fact interface
interface PetFact {
  _id: string;
  title: string;
  content: string;
  blogPost: string;
  category: 'health' | 'nutrition' | 'behavior' | 'general' | 'fun';
  type: 'medical' | 'lifestyle' | 'behavioral';
  petCategory?: string;
  tip?: string;
  generatedAt: string;
  isReadByUser: boolean;
}

const StyledCard = styled(Card)(({ theme }: { theme: Theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(3),
  boxShadow: theme.customShadows.card,
  '&:hover': {
    boxShadow: theme.customShadows.z16,
  },
}));

// Define feedback interface
interface FeedbackState {
  isSubmitting: boolean;
  isSubmitted: boolean;
  isHelpful?: boolean;
}

const FeedbackBox = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
}));

export default function PetFactBlogPage() {
  const theme = useTheme();
  const { factId } = useParams<{ factId: string }>();
  const [petFact, setPetFact] = useState<PetFact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<FeedbackState>({
    isSubmitting: false,
    isSubmitted: false,
  });
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchPetFact();
  }, [factId]);

  const fetchPetFact = async () => {
    if (!factId) return;

    try {
      setLoading(true);
      setError(null);

      // Use the generated API client
      const response = await suggestionApi.suggestionControllerGetPetFactById(
        factId,
      );
      setPetFact(response.data);

      // Mark the pet fact as read if it's not already read
      if (response.data && !response.data.isReadByUser) {
        try {
          await userApi.userControllerMarkFactAsRead({
            factReadStatusDto: { factId: response.data._id },
          });
          // Update the local state to reflect that the fact has been read
          setPetFact((prev) => (prev ? { ...prev, isReadByUser: true } : prev));
        } catch (markError) {
          console.error('Error marking pet fact as read:', markError);
          // Continue showing the fact even if marking as read fails
        }
      }

      // Check if feedback has already been submitted for this fact
      try {
        if (response.data) {
          const feedbackStatus = await feedbackApi.hasGivenFeedback(
            response.data._id,
          );
          // Update feedback state with both hasGiven and isHelpful values
          setFeedback((prev) => ({ 
            ...prev, 
            isSubmitted: feedbackStatus.hasGiven,
            isHelpful: feedbackStatus.isHelpful 
          }));
        }
      } catch (feedbackError) {
        console.error('Error checking feedback status:', feedbackError);
      }
    } catch (err) {
      console.error('Error fetching pet fact:', err);
      setError(translateKey("hardcoded.nie_udalo_sie_zaladowac_faktu_o_zwierzetach"));
      enqueueSnackbar(translateKey("hardcoded.nie_udalo_sie_zaladowac_faktu_o_zwierzetach"), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFeedback = async (isHelpful: boolean) => {
    if (!petFact || feedback.isSubmitting) return;

    try {
      setFeedback({ isSubmitting: true, isSubmitted: feedback.isSubmitted, isHelpful: feedback.isHelpful });
      await feedbackApi.submitFeedback({
        suggestionId: petFact._id,
        isHelpful,
      });

      setFeedback({ isSubmitting: false, isSubmitted: true, isHelpful });
      enqueueSnackbar(translateKey("petAi.feedbackThanks"), { variant: 'success' });
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setFeedback({ isSubmitting: false, isSubmitted: feedback.isSubmitted, isHelpful: feedback.isHelpful });
    }
  };

  return (
    <>
      <Helmet>
        <title>
          {petFact ? petFact.title : translateKey("hardcoded.fakt_o_zwierzetach")} | Woof Woof
        </title>
      </Helmet>

      <Container maxWidth="lg">
        <Box sx={{ mb: 3, mt: 3 }}>
          <Button
            variant="contained"
            sx={{
              background: theme.palette.common.black,
              color: theme.palette.common.white,
            }}
            size="medium"
            component={RouterLink}
            to={PATH_DASHBOARD.root}
            startIcon={<Iconify icon="eva:arrow-back-fill" />}
          >{translateKey("hardcoded.powrot_do_strony_glownej")}</Button>
        </Box>

        {loading ? (
          <StyledCard>
            <CardContent>
              <Skeleton variant="text" height={60} sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
              <Stack direction="row" spacing={1}>
                <Skeleton variant="rectangular" width={80} height={30} />
                <Skeleton variant="rectangular" width={80} height={30} />
              </Stack>
            </CardContent>
          </StyledCard>
        ) : error ? (
          <StyledCard>
            <CardContent>
              <Typography variant="h6" color="error" align="center">
                {error}
              </Typography>
            </CardContent>
          </StyledCard>
        ) : petFact ? (
          <StyledCard>
            <CardContent>
              <Typography variant="h4" color="text.primary" gutterBottom>
                {petFact.title}
              </Typography>

              {petFact.generatedAt && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Dodano: {fDate(petFact.generatedAt)}
                </Typography>
              )}

              <Stack direction="row" spacing={1}>
                <TagChip
                  label={`#${
                    PET_FACT_TYPE_TRANSLATIONS[petFact.type] ||
                    petFact.category.charAt(0).toUpperCase() +
                      petFact.category.slice(1)
                  }`}
                  color="secondary"
                  size="small"
                />
                {petFact.petCategory && (
                  <TagChip
                    label={`#${
                      PET_CATEGORY_TRANSLATIONS[petFact.petCategory] ||
                      petFact.petCategory.charAt(0).toUpperCase() +
                        petFact.petCategory.slice(1)
                    }`}
                    color="secondary"
                    size="small"
                  />
                )}
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mt: 2 }}>
                {petFact.blogPost ? (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {petFact.blogPost}
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body1">{translateKey("hardcoded.nie_znaleziono_zawartosci")}</Typography>
                  </>
                )}
              </Box>

              {petFact.tip && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: 'primary.lighter',
                    display: 'flex',
                    alignItems: 'flex-start',
                  }}
                >
                  <Iconify
                    icon="mdi:lightbulb"
                    width={24}
                    height={24}
                    sx={{ color: 'primary.dark', mr: 1 }}
                  />
                  <Typography variant="body1" fontWeight="medium">
                    {petFact.tip}
                  </Typography>
                </Box>
              )}
            </CardContent>
            {/* Feedback section */}
            <Divider />
            <FeedbackBox>
              <Typography variant="body2">{translateKey("petAi.feedbackPrompt")}</Typography>

              <Box>
                <IconButton
                  onClick={() => handleFeedback(true)}
                  disabled={feedback.isSubmitting}
                  sx={{ mr: 1 }}
                >
                  <ThumbUpIcon
                    fontSize="small"
                    color={
                      feedback.isSubmitted && feedback.isHelpful
                        ? 'primary'
                        : 'disabled'
                    }
                  />
                </IconButton>
                <IconButton
                  onClick={() => handleFeedback(false)}
                  disabled={feedback.isSubmitting}
                >
                  <ThumbDownIcon
                    fontSize="small"
                    color={
                      feedback.isSubmitted && !feedback.isHelpful
                        ? 'primary'
                        : 'disabled'
                    }
                  />
                </IconButton>
              </Box>
            </FeedbackBox>
          </StyledCard>
        ) : (
          <StyledCard>
            <CardContent>
              <Typography variant="h6" color="text.secondary" align="center">{translateKey("hardcoded.nie_znaleziono_faktu_o_zwierzetach")}</Typography>
            </CardContent>
          </StyledCard>
        )}
      </Container>
    </>
  );
}
